import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Divider } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { exportToFile } from "../../services/api/exportToExcel";
import dayjs from "dayjs"; // Import dayjs
import { useDispatch, useSelector } from "react-redux";
import ReportTable from "./ReportTable";
import PaginationManager from "../../components/newComp/Pagination";
import { showToast } from "../../store/slices/toastSlice";
import { setPage, setRowsPerPage } from "../../store/slices/paginationSlice";
import { getChartData } from "../../services/api/invoice";
import ReportFiltersMonthDate from "./ReportFiltersMonthDate";

const ReportList = () => {
  const dispatch = useDispatch();
  const { page, rowsPerPage } = useSelector((state) => state.pagination);

  const [reportList, setReportList] = useState([]);
  const [filteredReportList, setFilteredReportList] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [monthYearList, setMonthYearList] = useState([]); // List of unique month-year options
  const [selectMonthYear, setSelectMonthYear] = useState([]); // Selected month-year(s)

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const reportsData = await getChartData();
        setReportList(reportsData.data);
        setFilteredReportList(reportsData.data);

        // Generate month-year list for the dropdown
        const monthYearData = generateMonthYearList(reportsData.data);
        setMonthYearList(monthYearData);
      } catch (error) {
        dispatch(showToast({ message: error.message, severity: "error" }));
      }
    };

    fetchInitialData();
  }, []);

  // Generate a list of unique months and years from the data
  const generateMonthYearList = (data) => {
    const monthYearSet = new Set();
    data.forEach((entry) => {
      monthYearSet.add(entry.month); // Add the month (e.g., "JANUARY 2025")
    });
    return [...monthYearSet];
  };

  // Filter the data based on selected month(s)
  useEffect(() => {
    filterDataByMonthYear();
  }, [selectMonthYear]);

  const filterDataByMonthYear = () => {
    if (selectMonthYear.length === 0) {
      setFilteredReportList(reportList); // Show all if no month is selected
    } else {
      const filtered = reportList.filter((entry) =>
        selectMonthYear.includes(entry.month)
      );
      setFilteredReportList(filtered);
    }
  };

  const handleSearch = () => {
    let filtered = reportList.filter((entry) => isEntryInDateRange(entry));
    setFilteredReportList(filtered);
  };

  const isEntryInDateRange = (entry) => {
    const entryDate = dayjs(entry.entryDate.split("T")[0], "DD/MM/YYYY");
    if (selectedDate && entryDate.isSame(selectedDate, "day")) return true;
    if (startDate && endDate) {
      return entryDate.isBetween(startDate, endDate, null, "[]");
    }
    return false;
  };

  const handleDateChange = (newValue, type) => {
    if (type === "selectedDate") {
      setSelectedDate(newValue);
      setStartDate(null);
      setEndDate(null);
    } else if (type === "startDate" || type === "endDate") {
      setStartDate(type === "startDate" ? newValue : startDate);
      setEndDate(type === "endDate" ? newValue : endDate);
      setSelectedDate(null);
    }
  };

  const clearDateFilters = () => {
    setSelectedDate(null);
    setStartDate(null);
    setEndDate(null);
    setFilteredEntries(entries); // Reset filtered entries when clearing filters
  };

  // Pagination logic
  const handleChangePage = (event, newPage) => {
    dispatch(setPage(newPage));
  };

  const handleExport = async (format, type) => {
    const token = localStorage.getItem("token");

    // Prepare the date parameters
    const dateParams = {};
    if (selectedDate) {
      dateParams.selectedDate = selectedDate.format("YYYY-MM-DD"); // or your preferred format
    }
    if (startDate && endDate) {
      dateParams.startDate = startDate.format("YYYY-MM-DD");
      dateParams.endDate = endDate.format("YYYY-MM-DD");
    }

    try {
      const blob = await exportToFile(
        type,
        token,
        format,
        selectedDate,
        startDate,
        endDate
      );
      const url = window.URL.createObjectURL(blob);
      const fileExtension = format === "pdf" ? "pdf" : "xlsx";
      const fileName = `${type}_entries.${fileExtension}`;

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
    dispatch(setPage(0)); // Reset to page 0 when rows per page changes
  };

  // Slice filtered data based on current page and rows per page
  const currentPageData = filteredReportList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              mb: 3,
              p: 2.5,
              borderRadius: 2,
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            }}
          >
            {/* <ReportFilters
            selectedDate={selectedDate}
            startDate={startDate}
            endDate={endDate}
            handleDateChange={handleDateChange}
            handleSearch={handleSearch}
            clearDateFilters={clearDateFilters}
            handleExport={() => {}}
          /> */}

            <ReportFiltersMonthDate
              monthYearList={monthYearList}
              selectMonthYear={selectMonthYear}
              setSelectMonthYear={setSelectMonthYear}
              handleExport={() => {}}
            />
          </Paper>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "#333" }}
              >
                Report List
              </Typography>
            </Box>
          </Box>

          {/* Table Section */}
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              borderRadius: 2,
              backgroundColor: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
                p: 2,
              }}
            >
              <ReportTable rows={currentPageData} />
            </Box>

            <Divider />

            <PaginationManager
              count={filteredReportList.length} // Total count of filtered data
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Box>
      </LocalizationProvider>
    </>
  );
};

export default ReportList;
